import React, { useEffect, useRef, useState } from 'react';
import './ImageWithBackground.css';

// In-memory cache shared across renders
const videoBlobCache = new Map();

const ImageWithBackground = ({
    src,
    image_radius,
    className,
    image_format,
    metadata,
    isVideo
}) => {
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const abortRef = useRef(null);

    const isAspectRatioSignificantlyDifferent = () => {
        const defaultAspectRatio = 202 / 308;
        const tolerance = 0.05;
        return metadata?.extra?.size
            ? Math.abs(defaultAspectRatio - (metadata.extra.size.width / metadata.extra.size.height)) > tolerance
            : false;
    };

    const imageStyle = isAspectRatioSignificantlyDifferent()
        ? metadata.extra.size?.width > metadata.extra.size?.height
            ? {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
            }
            : {
                width: '100%',
                height: '100%',
                objectFit: 'scale-down',
            }
        : {
            width: 'unset',
            height: '100%',
            objectFit: 'cover',
        };

    const backgroundColor =
        metadata?.first_frame_db?.metadata?.extra?.pixels
            ? `rgb(${metadata.first_frame_db.metadata.extra.pixels.r},${metadata.first_frame_db.metadata.extra.pixels.g},${metadata.first_frame_db.metadata.extra.pixels.b})`
            : metadata?.extra?.pixels
                ? `rgb(${metadata.extra.pixels.r},${metadata.extra.pixels.g},${metadata.extra.pixels.b})`
                : 'transparent';

    const previewImage = metadata?.first_frame_db?.urls?.orig;

    useEffect(() => {
        if (!isVideo) return;

        // Already cached?
        if (videoBlobCache.has(src)) {
            setVideoUrl(videoBlobCache.get(src));
            return;
        }

        // Fetch video as blob and cache it
        abortRef.current = new AbortController();
        fetch(src, { signal: abortRef.current.signal })
            .then(res => res.blob())
            .then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                videoBlobCache.set(src, blobUrl);
                setVideoUrl(blobUrl);
            })
            .catch(err => {
                if (err.name !== 'AbortError') {
                    console.error('Video fetch failed:', err);
                }
            });

        return () => {
            abortRef.current?.abort();
        };
    }, [src, isVideo]);

    return (
        <div
            className={className}
            style={{
                backgroundColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: image_radius,
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {isVideo ? (
                <>
                    {!isVideoLoaded && previewImage && (
                        <img
                            className="image-with-background__img"
                            src={previewImage}
                            alt=""
                            style={{
                                ...imageStyle,
                                position: 'absolute',
                                overflow: 'hidden'
                            }}
                        />
                    )}
                    {videoUrl && (
                        <video
                            className="image-with-background__img"
                            src={videoUrl}
                            style={imageStyle}
                            muted
                            playsInline
                            autoPlay
                            loop
                            controls={false}
                            disablePictureInPicture
                            controlsList="nodownload nofullscreen noremoteplayback"
                            onCanPlayThrough={() => setIsVideoLoaded(true)}
                        />
                    )}
                </>
            ) : (
                <img
                    className="image-with-background__img"
                    style={imageStyle}
                    src={src}
                    alt=""
                />
            )}
        </div>
    );
};

export default ImageWithBackground;
